import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { AvailabilityCalendar } from '../components/AvailabilityCalendar';
import { MedicalNotesPanel } from '../components/MedicalNotesPanel';
import { AvailabilityEditor } from '../components/AvailabilityEditor';
import { PhysioBookingsPanel } from '../components/PhysioBookingsPanel';
import { OnlineToggle } from '../components/OnlineToggle';
import { NotificationBell } from '../components/NotificationBell';
import { Calendar, Users, Video, Phone, Settings, HandHeart } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const DoctorDashboard = () => {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  // Tab lives in the URL so the mobile bottom bar can link straight to it.
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'calendar';
  const setActiveTab = (v) => setSearchParams({ tab: v }, { replace: true });
  const [appointments, setAppointments] = useState([]);
  const [todayAppointments, setTodayAppointments] = useState([]);
  const [callQueue, setCallQueue] = useState([]);

  useEffect(() => {
    fetchAll();
    const interval = setInterval(fetchAll, 15000);
    return () => clearInterval(interval);
  }, []);

  const fetchAll = async () => {
    try {
      const [aptRes, queueRes] = await Promise.all([
        axios.get(`${API}/appointments`, { withCredentials: true }),
        axios.get(`${API}/calls/queue`, { withCredentials: true }).catch(() => ({ data: [] }))
      ]);
      setAppointments(aptRes.data);
      setCallQueue(queueRes.data);
      const today = new Date().toISOString().split('T')[0];
      setTodayAppointments(aptRes.data.filter(a => a.appointment_date === today));
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleAcceptCall = async (callId) => {
    try {
      await axios.post(`${API}/calls/${callId}/accept`, {}, { withCredentials: true });
      navigate(`/doctor/call/${callId}`);
    } catch (e) {
      console.error(e);
    }
  };

  const handleJoinCall = (callId) => {
    navigate(`/doctor/call/${callId}`);
  };

  // Psychology-only: doctor kicks off a video call to the patient right from
  // the appointment row. Backend creates the active call_queue row + notifies
  // the patient. Doctor is bounced straight into the call room.
  const handleStartPsychologyCall = async (appointmentId, callType = 'video') => {
    try {
      const r = await axios.post(
        `${API}/psychology/appointments/${appointmentId}/start-call`,
        { call_type: callType },
        { withCredentials: true },
      );
      if (r.data?.call_id) {
        navigate(`/doctor/call/${r.data.call_id}`);
      }
    } catch (e) {
      console.error('Failed to start psychology call:', e);
      const msg = e.response?.data?.detail || t('فشل بدء المكالمة', 'Failed to start call', 'Échec');
      alert(msg);
    }
  };

  const isPsychologyDoctor = user?.category === 'psychology';

  return (
    <div className={`min-h-screen bg-gray-50 ${language === 'ar' ? 'rtl' : 'ltr'}`}>
      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8 gap-4">
          <div>
            <h1 className="text-3xl font-bold text-[#2c3e50] mb-2" data-testid="doctor-dashboard-title">
              {t('لوحة تحكم الطبيب', 'Doctor Dashboard', 'Tableau du médecin')}
            </h1>
            <p className="text-gray-600">{t('مرحباً دكتور', 'Welcome Dr.', 'Bienvenue Dr.')} {user?.full_name}</p>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center gap-3 w-full md:w-auto">
            <div className="flex items-center gap-3">
              <NotificationBell />
              <Link to="/doctor/profile" className="flex-1 sm:flex-none">
                <Button variant="outline" className="w-full" data-testid="edit-profile-button">
                  <Settings size={18} className={language === 'ar' ? 'ml-2' : 'mr-2'} />
                  {t('الملف الشخصي', 'Profile', 'Profil')}
                </Button>
              </Link>
            </div>
            <div className="w-full sm:w-auto">
              <OnlineToggle />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">{t('مواعيد اليوم', "Today's Appointments", "Aujourd'hui")}</p>
                <p className="text-3xl font-bold text-[#1e88e5]">{todayAppointments.length}</p>
              </div>
              <Calendar className="text-[#1e88e5]" size={40} />
            </div>
          </Card>
          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">{t('إجمالي المواعيد', 'Total', 'Total')}</p>
                <p className="text-3xl font-bold text-[#1e88e5]">{appointments.length}</p>
              </div>
              <Users className="text-[#1e88e5]" size={40} />
            </div>
          </Card>
          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">{t('مكالمات نشطة', 'Active Calls', 'Appels actifs')}</p>
                <p className="text-3xl font-bold text-green-600">{callQueue.filter(c => c.status === 'active').length}</p>
              </div>
              <Video className="text-green-600" size={40} />
            </div>
          </Card>
          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">{t('قائمة الانتظار', 'Waiting', 'Attente')}</p>
                <p className="text-3xl font-bold text-yellow-600">{callQueue.filter(c => c.status === 'waiting').length}</p>
              </div>
              <Phone className="text-yellow-600" size={40} />
            </div>
          </Card>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="w-full flex overflow-x-auto no-scrollbar gap-1 justify-start md:grid md:grid-cols-5">
            <TabsTrigger value="calendar" data-testid="tab-calendar">{t('التقويم', 'Calendar', 'Calendrier')}</TabsTrigger>
            <TabsTrigger value="availability" data-testid="tab-availability">{t('التوفر', 'Availability', 'Disponibilité')}</TabsTrigger>
            <TabsTrigger value="appointments" data-testid="tab-appointments">{t('المواعيد', 'Appointments', 'Rendez-vous')}</TabsTrigger>
            <TabsTrigger value="calls" data-testid="tab-calls">{t('المكالمات', 'Calls', 'Appels')}</TabsTrigger>
            <TabsTrigger value="notes" data-testid="tab-notes">{t('الملاحظات', 'Notes', 'Notes')}</TabsTrigger>
            {user?.category === 'physiotherapy' && (
              <TabsTrigger value="physio" data-testid="tab-physio">
                <HandHeart size={14} className="me-1" />
                {t('علاج فيزيائي', 'Physio', 'Physio')}
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="calendar" className="mt-4">
            <AvailabilityCalendar appointments={appointments} />
          </TabsContent>

          <TabsContent value="availability" className="mt-4">
            <AvailabilityEditor />
          </TabsContent>

          <TabsContent value="appointments" className="space-y-4 mt-4">
            {appointments.length === 0 ? (
              <Card className="p-6"><p className="text-gray-600">{t('لا توجد مواعيد', 'No appointments', 'Aucun rendez-vous')}</p></Card>
            ) : appointments.map((apt) => (
              <Card key={apt._id} className="p-4">
                <div className="flex justify-between items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-lg">
                      {isPsychologyDoctor && apt.category === 'psychology'
                        ? t('المريض', 'Patient', 'Patient')
                        : apt.patient_name}
                    </h3>
                    <p className="text-gray-600">{apt.appointment_date} - {apt.appointment_time}</p>
                    <p className="text-sm text-gray-500">{apt.consultation_type}</p>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <span className={`px-3 py-1 rounded-full text-sm ${
                      apt.status === 'completed' ? 'bg-green-100 text-green-800' :
                      apt.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>{apt.status}</span>
                    {isPsychologyDoctor && apt.category === 'psychology' && apt.status !== 'completed' && (
                      <Button
                        size="sm"
                        onClick={() => handleStartPsychologyCall(apt._id, 'video')}
                        className="bg-[#1e88e5] hover:bg-[#1565c0] text-white"
                        data-testid={`start-psychology-call-${apt._id}`}
                      >
                        <Video size={14} className={language === 'ar' ? 'ml-1' : 'mr-1'} />
                        {t('بدء مكالمة فيديو', 'Start Video Call', 'Démarrer appel vidéo')}
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </TabsContent>

          <TabsContent value="calls" className="space-y-4 mt-4">
            {callQueue.length === 0 ? (
              <Card className="p-6"><p className="text-gray-600">{t('لا توجد مكالمات', 'No incoming calls', 'Aucun appel')}</p></Card>
            ) : callQueue.map((call) => (
              <Card key={call._id} className="p-4">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="font-bold">{call.patient_name}</h3>
                    <p className="text-sm text-gray-600 flex items-center gap-1">
                      {call.call_type === 'video' ? <Video size={14} /> : <Phone size={14} />}
                      {call.call_type === 'video' ? t('فيديو', 'Video', 'Vidéo') : t('صوت', 'Voice', 'Voix')}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">{call.medical_form?.current_symptoms}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-3 py-1 rounded-full text-xs ${
                      call.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                    }`}>{call.status}</span>
                    {call.status === 'waiting' && (
                      <Button size="sm" onClick={() => handleAcceptCall(call._id)} className="bg-[#1e88e5] hover:bg-[#1565c0]" data-testid={`accept-call-${call._id}`}>
                        {t('قبول وانضمام', 'Accept & Join', 'Accepter')}
                      </Button>
                    )}
                    {call.status === 'active' && (
                      <Button size="sm" onClick={() => handleJoinCall(call._id)} className="bg-green-600 hover:bg-green-700 text-white" data-testid={`join-call-${call._id}`}>
                        <Video size={14} className={language === 'ar' ? 'ml-1' : 'mr-1'} />
                        {t('انضمام', 'Join', 'Rejoindre')}
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </TabsContent>

          <TabsContent value="notes" className="mt-4">
            <MedicalNotesPanel />
          </TabsContent>

          {user?.category === 'physiotherapy' && (
            <TabsContent value="physio" className="mt-4" data-testid="physio-tab-content">
              <PhysioBookingsPanel />
            </TabsContent>
          )}
        </Tabs>
      </div>
    </div>
  );
};
